/**
 * AssistantsMenu — топбарное меню «Помощники» (AI / Мессенджер / AI-коуч / Поддержка).
 *
 * Заменяет четыре конкурирующих FAB-кнопки в правом нижнем углу (AIChatWidget
 * `.ai-fab`, ChatPanel `.chat-toggle`, AiCoachPane `.ai-coach-fab`, SupportWidget
 * fab), которые перекрывали sidebar/контент.
 *
 * Industry pattern 2024-2026: Microsoft Copilot (M365/Dynamics), Salesforce
 * Einstein, ServiceNow Now Assist, Atlassian Intelligence, ClickUp Brain,
 * GitHub Copilot Chat — все используют topbar launcher + right-side drawer.
 * FAB в правом нижнем углу — паттерн customer-support виджетов (Intercom/Drift),
 * не workspace AI.
 *
 * Контракт открытия:
 *   - AI-ассистент:  window.dispatchEvent(new CustomEvent('onboardix:ai-assistant:open'))
 *   - Мессенджер:    window.dispatchEvent(new CustomEvent('onboardix:messenger:open'))
 *   - AI-коуч:       prop `onOpenAiCoach()` — state живёт в Layout
 *   - Поддержка:     window.dispatchEvent(new CustomEvent('onboardix:support:open'))
 *
 * Дизайн: DS v2 — `.layout-dropdown`, `var(--color-accent)` var(--color-purple), Lucide-иконки,
 * никаких эмодзи. Бейдж непрочитанных мессенджер-сообщений виден на trigger-кнопке
 * (передаётся через prop `messengerUnread`).
 */
import { useEffect, useRef, useState } from 'react'
import { Wand2, Bot, MessageCircle, Brain, LifeBuoy, ChevronRight } from 'lucide-react'
import './assistants-menu.css'

const dispatch = (name) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(name))
    }
}

export default function AssistantsMenu({ onOpenAiCoach, messengerUnread = 0 }) {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)

    useEffect(() => {
        if (!open) return undefined
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        const onEsc = (e) => {
            if (e.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', onClickOutside)
        document.addEventListener('keydown', onEsc)
        return () => {
            document.removeEventListener('mousedown', onClickOutside)
            document.removeEventListener('keydown', onEsc)
        }
    }, [open])

    // Цвета чипов через theme-токены — адаптируются под текущую тему
    // (default/sandy/dark). Семантика: accent = AI, info = чат, success =
    // развитие/коуч, warning = поддержка.
    const items = [
        {
            key: 'ai',
            icon: Bot,
            tone: 'var(--color-accent)',
            toneBg: 'var(--color-accent-bg)',
            title: 'AI-ассистент',
            subtitle: 'Поиск по базе знаний, HR и compliance',
            onSelect: () => dispatch('onboardix:ai-assistant:open'),
        },
        {
            key: 'messenger',
            icon: MessageCircle,
            tone: 'var(--color-info)',
            toneBg: 'var(--color-info-bg)',
            title: 'Мессенджер',
            subtitle: 'Чат с коллегами и каналы команды',
            badge: messengerUnread > 0 ? (messengerUnread > 99 ? '99+' : String(messengerUnread)) : null,
            onSelect: () => dispatch('onboardix:messenger:open'),
        },
        {
            key: 'coach',
            icon: Brain,
            tone: 'var(--color-success)',
            toneBg: 'var(--color-success-bg)',
            title: 'AI-коуч развития',
            subtitle: 'Навыки, цели, индивидуальный план',
            onSelect: () => { if (onOpenAiCoach) onOpenAiCoach() },
        },
        {
            key: 'support',
            icon: LifeBuoy,
            tone: 'var(--color-warning)',
            toneBg: 'var(--color-warning-bg)',
            title: 'Поддержка',
            subtitle: 'Тикет в helpdesk со скриншотом',
            onSelect: () => dispatch('onboardix:support:open'),
        },
    ]

    const handleSelect = (item) => {
        setOpen(false)
        item.onSelect()
    }

    return (
        <div className="assistants-menu" ref={wrapRef}>
            <button
                type="button"
                className={`assistants-menu__trigger${open ? ' assistants-menu__trigger--active' : ''}`}
                onClick={() => setOpen((v) => !v)}
                title="Помощники"
                aria-label="Помощники"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <Wand2 size={18} />
                {messengerUnread > 0 && !open && (
                    <span className="assistants-menu__trigger-badge">
                        {messengerUnread > 99 ? '99+' : messengerUnread}
                    </span>
                )}
            </button>

            {open && (
                <div className="layout-dropdown assistants-menu__dropdown" role="menu">
                    <div className="assistants-menu__header">
                        <div className="assistants-menu__header-title">
                            <Wand2 size={14} /> Помощники
                        </div>
                        <div className="assistants-menu__header-hint">
                            Выберите, где работать
                        </div>
                    </div>
                    <div className="assistants-menu__list">
                        {items.map((item) => {
                            const Icon = item.icon
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="menuitem"
                                    className="assistants-menu__item"
                                    onClick={() => handleSelect(item)}
                                >
                                    <span
                                        className="assistants-menu__item-icon"
                                        style={{ background: item.toneBg, color: item.tone }}
                                    >
                                        <Icon size={18} />
                                    </span>
                                    <span className="assistants-menu__item-body">
                                        <span className="assistants-menu__item-title">
                                            {item.title}
                                            {item.badge && (
                                                <span className="assistants-menu__item-badge">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </span>
                                        <span className="assistants-menu__item-subtitle">
                                            {item.subtitle}
                                        </span>
                                    </span>
                                    <ChevronRight
                                        size={14}
                                        className="assistants-menu__item-chevron"
                                    />
                                </button>
                            )
                        })}
                    </div>
                    <div className="assistants-menu__footer">
                        <kbd>Ctrl</kbd>+<kbd>K</kbd> — командное меню
                    </div>
                </div>
            )}
        </div>
    )
}
